import { useState, type FormEvent } from "react";
import { useSession } from "../state/session.js";
import { ApiError } from "../api/http.js";
import { useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { Panel } from "../ui/Panel.js";
import { Link } from "../router.js";
import { AuthFrame } from "./AuthFrame.js";

export function errorKey(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "NAME_TAKEN") return "auth.error.NAME_TAKEN";
    if (e.code.startsWith("INVITE_")) return `auth.error.${e.code}`;
    if (e.status === 401) return "auth.error.UNAUTHORIZED";
    if (e.code === "VALIDATION" || e.status === 400) return "auth.error.VALIDATION";
  }
  return "auth.error.generic";
}

export function Login() {
  const t = useT();
  const login = useSession((s) => s.login);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(name.trim(), password);
    } catch (err) {
      setError(t(errorKey(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame>
      <Panel title={t("auth.login")}>
        <form onSubmit={submit} data-testid="login-form">
          <div className="field">
            <label htmlFor="login-name">{t("auth.name")}</label>
            <input id="login-name" autoComplete="username" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
          <div className="field">
            <label htmlFor="login-password">{t("auth.password")}</label>
            <input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error ? <p className="error" role="alert">{error}</p> : null}
          <Button type="submit" variant="primary" loading={busy} style={{ width: "100%" }}>
            {busy ? t("auth.submitting") : t("auth.login")}
          </Button>
        </form>
        <p className="dim" style={{ marginTop: 16, fontSize: 14 }}>
          {t("auth.noAccount")} <Link to="/register">{t("auth.register")}</Link>
        </p>
      </Panel>
    </AuthFrame>
  );
}
